import { injectable, inject, postConstruct } from "inversify";
import { Client, ClientConfigType, ClientEvent } from "~/modules/client";
import { Role } from "~/modules/auth";
import { accountConfigRTTI, AccountConfig } from "~/modules/config";
import { Twilsock, twilsockRTTI, TwilsockEvent } from "~/modules/websocket";
import { Session, SessionEvent, sessionRTTI } from "~/modules/session";
import { Logger, loggerFactoryRTTI, LoggerFactory, LoggerName } from "~/modules/logger";
import { TelemetryClient, TelemetryClientFactory, telemetryClientFactoryRTTI } from "~/modules/telemetry";
import {
    telemetrySdkClientRTTI,
    TelemetrySdkEvent,
    TelemetrySdkEventGroup,
    TelemetrySdkClient,
    TelemetrySdkEventName,
    TelemetrySdkEventSource
} from "~/modules/telemetrySdkClient";
import { RealtimeStats, realtimeStatsRTTI } from "~/modules/stats";
import { Emitter, eventEmitterRTTI, proxyEvent } from "~/modules/events";
import { Enumerable } from "~/utils/decorators/Enumerable";

@injectable()
export class ClientImpl implements Client {
    readonly #session: Session;

    readonly #connection: Twilsock;

    readonly #logger: Logger;

    readonly #telemetryClientFactory: TelemetryClientFactory<any>; 

    public readonly config: ClientConfigType;

    readonly #telemetrySdkClient: TelemetrySdkClient;

    readonly realtimeStats: RealtimeStats;

    readonly #emitter: Emitter;

    constructor(
        @inject(sessionRTTI) session: Session,
        @inject(twilsockRTTI) connection: Twilsock,
        @inject(accountConfigRTTI) accountConfig: AccountConfig,
        @inject(telemetryClientFactoryRTTI) telemetryClientFactory: TelemetryClientFactory<any>, 
        @inject(realtimeStatsRTTI) realtimeStats: RealtimeStats,
        @inject(telemetrySdkClientRTTI) telemetrySdkClient: TelemetrySdkClient,
        @inject(loggerFactoryRTTI) getLogger: LoggerFactory,
        @inject(eventEmitterRTTI) emitter: Emitter
    ) {
        this.#session = session;
        this.#connection = connection;
        this.config = {
            account: accountConfig
        };
        this.#telemetryClientFactory = telemetryClientFactory;
        this.realtimeStats = realtimeStats;
        this.#telemetrySdkClient = telemetrySdkClient;
        this.#logger = getLogger(LoggerName.Client);
        this.#emitter = emitter;
    }

    @postConstruct()
    setupProxies(): void {
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.TokenAboutToExpire, ClientEvent.TokenAboutToExpire);
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.TokenExpired, ClientEvent.TokenExpired);
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.TokenUpdated, ClientEvent.TokenUpdated);
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.ConnectionError, ClientEvent.ConnectionLost);
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.Connected, ClientEvent.ConnectionRestored);
        proxyEvent(this.#connection, this.#emitter, TwilsockEvent.Disconnected, ClientEvent.Disconnected);
        proxyEvent(this.#session, this.#emitter, SessionEvent.TokenAutoUpdateFailed, ClientEvent.TokenAutoUpdateFailed);
        proxyEvent(
            this.#session,
            this.#emitter,
            SessionEvent.TokenMaxLifetimeReached,
            ClientEvent.TokenMaxLifetimeReached
        );
    }

    async updateToken(token: string): Promise<void> {
        await this.#session.updateToken(token);
    }

    #sendDestroyEvent = async (): Promise<void> => {
        try {
            const telemetrySdkClient = this.#telemetrySdkClient;
            const group = telemetrySdkClient.createEventGroup<TelemetrySdkEvent>(TelemetrySdkEventGroup.Default);
            await group.addEvents({
                eventName: TelemetrySdkEventName.ClientDestroyed,
                eventSource: TelemetrySdkEventSource.Client
            });
        } catch (e) {
            this.#logger.error("Failed to send telemetry destroy event", e);
        }
    };

    async destroy(): Promise<void> {
        await this.#sendDestroyEvent();
        this.#logger.debug("client log out");
        await this.#session.destroy();
        this.#emitter.removeAllListeners();
    }

    @Enumerable
    get roles(): Array<Role> {
        return this.#session.roles;
    }

    @Enumerable
    get token(): string {
        return this.#session.token;
    }

    createTelemetryClient<U extends object>(name: string): TelemetryClient<U> {
        return this.#telemetryClientFactory(name);
    }

    on(eventName: ClientEvent, listener: (...args: unknown[]) => void): this {
        this.#emitter.on(eventName, listener);
        return this;
    }

    removeListener(eventName: ClientEvent, listener: (...args: unknown[]) => void): this {
        this.#emitter.removeListener(eventName, listener);
        return this;
    }
}
